import { useEffect, useRef, useState } from 'react';
import type { Explanation, VideoJob } from '@pen/shared';
import { api } from '../api';

const stageText: Record<VideoJob['stage'], string> = {
  queued: 'Waiting for a free slot…',
  planning: 'Choosing a small example to draw…',
  writing: 'Writing the animation…',
  voicing: 'Recording the narration…',
  drafting: 'Rendering a first draft…',
  draft: 'Draft ready. Polishing the final version…',
  polishing: 'Draft ready. Polishing the final version…',
  done: 'Ready.',
  failed: 'The animation could not be made.',
};

/** Follows a background animation job: plays the draft as soon as it exists and switches to the polished video,
 *  at the same moment, when that arrives. */
export function VideoExplanationView({ content }: { content: Extract<Explanation, { kind: 'video' }> }) {
  const [job, setJob] = useState<VideoJob>(content.job), [lost, setLost] = useState(false);
  const video = useRef<HTMLVideoElement>(null), resume = useRef<{ time: number; playing: boolean } | null>(null);
  useEffect(() => { setJob(content.job); }, [content]);
  useEffect(() => {
    if (job.stage === 'done' || job.stage === 'failed') return;
    let alive = true;
    const timer = window.setTimeout(() => {
      api.videoJob(job.id).then(next => { if (alive) { setLost(false); setJob(next); } }, () => { if (alive) setLost(true); });
    }, lost ? 10_000 : 4_000);
    return () => { alive = false; window.clearTimeout(timer); };
  }, [job, lost]);
  const src = job.videoUrl ?? job.draftUrl;
  // Remember where the viewer is before the source changes from draft to final.
  const shown = useRef<string | null>(null);
  if (src !== shown.current && video.current && shown.current) {
    resume.current = { time: video.current.currentTime, playing: !video.current.paused };
  }
  shown.current = src;
  const restore = () => {
    const v = video.current, r = resume.current;
    if (!v || !r) return;
    v.currentTime = Math.min(r.time, Math.max(0, (v.duration || r.time) - 0.2));
    if (r.playing) void v.play().catch(() => {});
    resume.current = null;
  };
  const minutes = Math.floor(job.elapsedSeconds / 60), seconds = String(job.elapsedSeconds % 60).padStart(2, '0');
  return <figure className="explanation-view video-explanation" aria-label={job.title ?? content.title}>
    <header><strong>{job.title ?? content.title}</strong>{!job.videoUrl && job.stage !== 'failed' && <small aria-live="polite">{minutes}:{seconds}</small>}</header>
    {src
      ? <video ref={video} key={src} src={src} controls playsInline preload="metadata" onLoadedMetadata={restore} />
      : job.stage !== 'failed' && <div className="video-waiting"><span className="video-pulse" aria-hidden="true" /></div>}
    <figcaption role="status">{job.stage === 'failed' ? job.error ?? stageText.failed : job.videoUrl ? null : stageText[job.stage]}
      {lost && job.stage !== 'failed' && ' (Reconnecting…)'}</figcaption>
    {job.draftUrl && !job.videoUrl && job.stage !== 'failed' && <p className="video-note">This is a quick draft; the sharper final version will replace it automatically.</p>}
  </figure>;
}
